'use client'

import { Button } from '@/core/components/form/button/button'
import Image from 'next/image'
import { useId } from 'react'

import { cn } from '@/core/utils/common'
import {
  chargeArt,
  chargeRewards,
} from '@/features/veluvit/lib/charge-products'

import type { PaymentProduct } from '@/features/veluvit/schemas/payment.schema'

type ChargeProductProps = {
  product: PaymentProduct
  tier: number
  disabled: boolean
  onSelect: () => void
}

export default function ChargeProduct({
  product,
  tier,
  disabled,
  onSelect,
}: ChargeProductProps) {
  const rewards = chargeRewards(product)
  const best = product.options?.badge === 'BEST'
  const discounted =
    product.baseAmount !== undefined && product.baseAmount > product.amount
  const detailId = useId()

  return (
    <li className="min-w-0">
      <Button
        type="button"
        aria-label={`${product.name}, ${product.amount.toLocaleString('ko-KR')}원 결제`}
        aria-describedby={detailId}
        disabled={disabled}
        onClick={onSelect}
        className={cn(
          'group relative flex size-full cursor-pointer flex-col items-center rounded-panel bg-foreground/[0.045] px-3 pt-4 pb-3 text-center transition-[background-color,transform] duration-200 ease-out',
          'hover:bg-foreground/[0.08] focus:outline-none focus-visible:bg-foreground/[0.1] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 over10:px-4 over10:pt-5 over10:pb-4',
          best && 'shadow-[inset_0_0_0_1px_rgb(245_194_107/0.32)]'
        )}
        variant="bare"
      >
        {best && (
          <span className="absolute top-2.5 left-2.5 rounded-full bg-sub/15 px-2 text-c11 font-bold leading-5 tracking-wide text-sub over10:top-3 over10:left-3 over10:text-c12">
            BEST
          </span>
        )}
        <span
          aria-hidden="true"
          className="flex h-20 w-full items-center justify-center over10:h-22"
        >
          <Image
            src={chargeArt(tier)}
            alt=""
            width={512}
            height={512}
            sizes="(min-width: 1001px) 88px, 80px"
            className="size-20 object-contain transition-transform duration-300 ease-out group-hover:-translate-y-1 group-hover:scale-105 over10:size-22"
          />
        </span>
        <span
          id={detailId}
          className="mt-2 flex min-h-12 w-full flex-col items-center over10:min-h-13"
        >
          {rewards.credits !== undefined ? (
            <span className="flex items-baseline justify-center gap-0.5 text-c20 font-bold leading-7 tabular-nums text-foreground over10:text-c23 over10:leading-8">
              {rewards.credits.toLocaleString('ko-KR')}
              <span className="text-c12 font-medium text-description over10:text-c13">
                젬
              </span>
            </span>
          ) : (
            <span className="text-c14 font-bold leading-5 text-foreground over10:text-c16">
              {product.name}
            </span>
          )}
          {rewards.firstBonus > 0 && (
            <span className="text-c12 font-semibold leading-5 tabular-nums text-sub over10:text-c13">
              첫 충전 +{rewards.firstBonus.toLocaleString('ko-KR')}
            </span>
          )}
          {rewards.bonus > 0 && (
            <span className="text-c12 leading-5 tabular-nums text-description over10:text-c13">
              보너스 +{rewards.bonus.toLocaleString('ko-KR')}
            </span>
          )}
        </span>
        <span className="mt-auto w-full pt-3">
          {discounted && (
            <span className="mb-1 block text-c12 leading-4 tabular-nums text-description line-through over10:text-c13">
              {product.baseAmount?.toLocaleString('ko-KR')}원
            </span>
          )}
          <span
            className={cn(
              'flex min-h-9 w-full items-center justify-center rounded-full text-c14 font-semibold tabular-nums transition-colors duration-200 over10:min-h-10 over10:text-c15',
              best
                ? 'bg-foreground text-background'
                : 'bg-foreground/10 text-foreground group-hover:bg-foreground/[0.16]'
            )}
          >
            {product.amount.toLocaleString('ko-KR')}원
          </span>
        </span>
      </Button>
    </li>
  )
}
